import React, { Component } from 'react';
import { strings } from '../lib';
import './patient.css';
import { List, Map } from 'immutable';
import { fonts } from '../styleguide';
import moment from 'moment-timezone';
import { phq9Score, mmas4Score } from '../business-logic';
import * as assets from '../assets';

const IntensityColors = List.of(
  '#F7F7F7',
  '#E6E6E6',
  '#FFD800',
  '#FFA350',
  '#FC5353'
);

const SleepQualityAnswers = Object.freeze({
  POOR: 'poor',
  NEUTRAL: 'neutral',
  GOOD: 'good'
});

const PhysicalActiveness = Object.freeze({
  NONE: 'none',
  LESS_THAN_NORM: 'lessThanNorm',
  NORMAL: 'normal',
  EXCEED_NORM: 'exceedNorm'
});

const AppetiteStatus = Object.freeze({
  DECREASED: 'decreased',
  NORMAL: 'normal',
  INCREASED: 'increased'
});

const AppetiteAmount = Object.freeze({
  LESS_THAN_NORM: 'lessThanNorm',
  NORM: 'norm',
  MORE_THAN_NORM: 'moreThanNorm'
});

function colorsForSleepQuality(val){
  return colorsForScale({
    [SleepQualityAnswers.POOR]: 'negative',
    [SleepQualityAnswers.NEUTRAL]: 'neutral',
    [SleepQualityAnswers.GOOD]: 'positive'
  }[val]);
}

function colorsForActivity(val){
  return colorsForScale({
    [PhysicalActiveness.NONE]: 'none',
    [PhysicalActiveness.LESS_THAN_NORM]: 'negative',
    [PhysicalActiveness.NORMAL]: 'neutral',
    [PhysicalActiveness.EXCEED_NORM]: 'positive'
  }[val]);
}

function colorsForAppetiteStatus(val){
  return colorsForScale({
    [AppetiteStatus.DECREASED]: 'negative',
    [AppetiteStatus.NORMAL]: 'neutral',
    [AppetiteStatus.INCREASED]: 'positive'
  }[val]);
}

function colorsForAppetiteAmount(val){
  return colorsForScale({
    [AppetiteAmount.LESS_THAN_NORM]: 'negative',
    [AppetiteAmount.NORM]: 'neutral',
    [AppetiteAmount.MORE_THAN_NORM]: 'positive'
  }[val]);
}

// TODO: algorithmically choose colors
function colorsForMeds(medNum) {
  switch( medNum ) {
    case 0: return '#4DD1FF';
    case 1: return '#97ECD6';
    default: return 'red';
  }
}

// TODO: algorithmically choose colors
function colorsForSideEffects(seNum) {
  switch( seNum ) {
    case 0: return '#A775FF';
    case 1: return '#FFD800';
    case 2: return '#FF6ED7';
    case 3: return '#FFA350';
    default: return 'red';
  }
}

// TODO: hardcoded for now, if we make adjustable will need to read from state
// TODO: move to model helper classes
const NUM_DAYS = 7 * 8; // 8 weeks
const PHQ9_INTERVAL_DAYS = 7 * 2;
const MMAS4_INTERVAL_DAYS = 7 * 1;
const SLEEP_INTERVAL_DAYS = 2;
const ACTIVITY_STEPS_INTERVAL_DAYS = 1;
const ACTIVITY_INTERVAL_DAYS = 2;
const APPETITE_INTERVAL_DAYS = 2;
const MEDS_INTERVAL_DAYS = 7 * 1;

const PHQ9_ANCHOR_INTERVAL_DAYS = PHQ9_INTERVAL_DAYS;
const MMAS4_ANCHOR_INTERVAL_DAYS = MMAS4_INTERVAL_DAYS;
const SLEEP_ANCHOR_INTERVAL_DAYS = 7 * 2;
const ACTIVITY_ANCHOR_INTERVAL_DAYS = 7 * 1;
const APPETITE_ANCHOR_INTERVAL_DAYS = 7 * 1;
const MEDS_ANCHOR_INTERVAL_DAYS = MEDS_INTERVAL_DAYS;

const Y_AXIS_WIDTH = 15;

function colorsForScale(val) {
  return {
    positive: {
      body: 'rgba(151,236,214,0.2)',
      marker: '#97ECD6'
    },
    neutral: {
      body: 'rgba(77,209,255,0.2)',
      marker: '#4DD1FF'
    },
    negative: {
      body: 'rgba(255,91,106,0.2)',
      marker: '#FF5B6A'
    },
    none: {
      body: null,
      marker: '#DDDDDD'
    }
  }[val];
}

const styles = {
  spacer: {
    display: 'flex',
    flex: 1
  }
};

const ControlValues = [ 'low', 'default', 'high'];
const ControlsShape = {
  ba: React.PropTypes.oneOf(ControlValues),
  mindfulness: React.PropTypes.oneOf(ControlValues),
  adherence: React.PropTypes.oneOf(ControlValues)
};

export default class Patient extends Component {

  static propTypes = {
    patient: React.PropTypes.any,
    day: React.PropTypes.number,
    controls: React.PropTypes.shape(ControlsShape),
    onUpdateControls: React.PropTypes.func.isRequired
  }
  
  render() {
    const { patient, controls, onUpdateControls } = this.props;
    return (
      <div className="container">
        <div className="nav-header">
          <div className="powered-by-container">
            <img src={assets.poweredBy} />
          </div>
        </div>
        <div className="header">
          <div className="user-header">
            <img src={assets.photoPlaceholder} />
            <p className="name">{patient && patient.get('firstName') + ' ' + patient.get('lastName')}</p>
          </div>
          <div className="controls-header">
            {this.renderControl('Behavioral Activation', 'ba', onUpdateControls)}
            {this.renderControl('Cognition / Mindfulness', 'mindfulness', onUpdateControls)}
            {this.renderControl('Adherence / Reminder', 'adherence', onUpdateControls)}
          </div>
        </div>
        <div className="content-container">
          {this.renderContainer({ title: strings.patient.phq9Title, renderer: this.renderPHQ9 })}
          {this.renderContainer({ title: strings.patient.medsTitle, renderer: this.renderMedications })}
          {this.renderContainer({ title: strings.patient.mmas4Title, renderer: this.renderMMAS4 })}
          {this.renderContainer({ title: strings.patient.sleepTitle, renderer: this.renderSleep })}
          {this.renderContainer({ title: strings.patient.activityTitle, renderer: this.renderActivity })}
          {this.renderContainer({ title: strings.patient.appetiteTitle, renderer: this.renderAppetite })}
        </div>
      </div>
    );
  }

  renderContainer(config) {
    const { title, renderer } = config;
    return (
      <div className="section-container">
        <p className="section-title">{title.toUpperCase()}</p>
        {renderer()}
      </div>
    );
  }

  renderMood = () => {
    return (
      <div className="mood-container">
        <div className="mood-spacer" />
        <div className="mood-circle">
          <div className="mood-inner-circle">
          </div>
        </div>
        <div className="mood-spacer">
          {this.renderIntensityLegend()}
        </div>
      </div>
    );
  };

  renderIntensityLegend = () => {
    const interval = 100 / IntensityColors.size;
    return (
      <div className="intensity-legend">
        <p className="legend-title">
          {strings.patient.moodLegendTitle.toUpperCase()}
        </p>
        <div className="intensity-bar">
          {IntensityColors.map((color) => (
              <div className="intensity-bar-section"
                   style={{ backgroundColor: color }}
                   key={color} />
          ))}
        </div>
        <div className="intensity-bar-axis">
          <p style={fonts.xAxis}
             key={interval * 0}>
            {interval * 0}
          </p>
          <div style={{display: 'flex',flex: 1}}/>
          <p style={fonts.xAxis}
             key={interval * 1}>
            {interval * 1}
          </p>
          <div style={{display: 'flex',flex: 1}}/>
          <p style={fonts.xAxis}
             key={interval * 2}>
            {interval * 2}
          </p>
          <div style={{display: 'flex',flex: 1}}/>
          <p style={fonts.xAxis}
             key={interval * 3}>
            {interval * 3}
          </p>
          <div style={{display: 'flex',flex: 1}}/>
          <p style={fonts.xAxis}
             key={interval * 4}>
            {interval * 4}
          </p>
          <div style={{display: 'flex',flex: 1}}/>
          <p style={fonts.xAxis}
             key={interval * 5}>
            {interval * 5}
          </p>
        </div>
      </div>
    );
  };
  
  renderPHQ9 = () => {
    const { patient } = this.props;
    const phq9s = ( patient && patient.get('phq9s') ) || List();
    const vals = this.getTimeAdjustedValues(
      phq9s,
      PHQ9_INTERVAL_DAYS,
      PHQ9_INTERVAL_DAYS,
      (phq9) => phq9.get('reportedAt'),
      (phq9) => phq9.get('reportedAtTZ'),
      (phq9) => phq9Score(phq9)
    );
    const baseline = this.getBaselineValue(
      phq9s,
      (phq9) => phq9.get('reportedAt'),
      (phq9) => phq9.get('reportedAtTZ'),
      (phq9) => phq9Score(phq9)
    );
    const phq9Link = 'http://www.phqscreeners.com/';
    return (
      <div>
        <p className="section-description"
           style={fonts.description}>
          {strings.patient.phq9Desc + ' '}
          <a style={fonts.description} href={phq9Link}>{phq9Link}</a>
        </p>
        {this.renderBarGraph({
          values: vals,
          yMin: 0,
          yMax: 27,
          numYAxisUnits: 4,
          yAxisLabels: { min: '0', max: '27', main: 'Score' },
          xMin: 0,
          xMax: NUM_DAYS,
          xRel: this.day(),
          numXAxisUnits: NUM_DAYS/PHQ9_INTERVAL_DAYS,
          xAxisValues: xAxisValues(NUM_DAYS, PHQ9_ANCHOR_INTERVAL_DAYS),
          showBaseline: true,
          baselineVal: baseline,
          colorsForValue: () => {
            return { body: 'rgba(203, 255, 15, 0.2)', marker: '#A8E603' };
          }
        })}
      </div>
    );
  };

  renderMedications = () => {
    const { patient } = this.props;
    const checkins = ( patient && patient.get('medicationCheckins') ) || List();
    const meds = checkins.map((chk) => chk.get('activeMedications'))
          .flatten(true)
          .reduce((red, m) => red.set(m.get('id'), m), new Map());
    const sideEffectsById = checkins.map((chk) => chk.get('sideEffects'))
          .flatten(true)
          .reduce((red, se) => red.set(se.get('id'), se), new Map());
    const medsList = meds.toList();
    const baselineChk = this.getBaselineValue(checkins,
                                              (chk) => chk.get('reportedAt'),
                                              (chk) => chk.get('reportedAtTZ'),
                                              (chk) => chk);
    const medValsById = meds.map((id, med) => {
      return this.getTimeAdjustedValues(
        checkins,
        MEDS_INTERVAL_DAYS,
        MEDS_INTERVAL_DAYS,
        (chk) => chk.get('reportedAt'),
        (chk) => chk.get('reportedAtTZ'),
        (chk) => chk.get('activeMedications').find((m) => m.id === med.id) ? 1 : 0
      );
    });
    const sideEffectValsById = sideEffectsById.map((id, se) => {
      return this.getTimeAdjustedValues(
        checkins,
        MEDS_INTERVAL_DAYS,
        MEDS_INTERVAL_DAYS,
        (chk) => chk.get('reportedAt'),
        (chk) => chk.get('reportedAtTZ'),
        (chk) => chk.get('sideEffects').find((s) => s.id === se.id) ? 1 : 0
      );
    });
    return (
      <div>
        {this.renderKey(medsList.map((m, i) => {
          return { name: m.get('name'), color: colorsForMeds(i) };
        }))}
        {this.renderHorizontalBarGraph({
          values: medsList,
          xMin: 0,
          xMax: NUM_DAYS,
          xRel: this.day(),
          numXAxisUnits: NUM_DAYS/MEDS_INTERVAL_DAYS,
          xAxisValues: xAxisValues(NUM_DAYS, MEDS_ANCHOR_INTERVAL_DAYS),
          xValueForValue: (med, idx) => {
            return medValsById.get(med.get('id'))[idx];
          },
          colorForValue: (m) => {
            return colorsForMeds(medsList.indexOf(m));
          },
          showBaseline: !meds.isEmpty(),
          baselineXValueForValue: (med) => {
            return !!medsList.find((m) => m.get('id') === med.get('id')) ? 1 : 0;
          },
          yAxisLabels: { main: 'Meds' }
        })}
        {this.renderXAxis(xAxisValues(NUM_DAYS, MEDS_ANCHOR_INTERVAL_DAYS))}
        {this.renderHorizontalBarGraph({
          values: sideEffectsById.toList(),
          xMin: 0,
          xMax: NUM_DAYS,
          xRel: this.day(),
          numXAxisUnits: NUM_DAYS/MEDS_INTERVAL_DAYS,
          xAxisValues: xAxisValues(NUM_DAYS, MEDS_ANCHOR_INTERVAL_DAYS),
          xValueForValue: (se, idx) => {
            return sideEffectValsById.get(se.get('id'))[idx];
          },
          colorForValue: (se) => {
            return colorsForSideEffects(sideEffectsById.toList().indexOf(se));
          },
          showBaseline: !sideEffectsById.isEmpty(),
          baselineXValueForValue: (sideEffect) => {
            return !!sideEffectsById.toList().find((se) => se.get('id') === sideEffect.get('id')) ? 1 : 0;
          },
          yAxisLabels: { main: 'Side Effects' }
        })}
        {this.renderKey(sideEffectsById.toList().map((se, i) => {
          return { name: se.get('name'), color: colorsForSideEffects(i) };
        }))}
      </div>
    );
  };
  
  renderMMAS4 = () => {
    const { patient } = this.props;
    const _checkins = patient && patient.get('medicationCheckins') || List();
    const checkins = _checkins.sortBy((c) => c.get('reportedAt'));
    const vals = this.getTimeAdjustedValues(
      checkins,
      MMAS4_INTERVAL_DAYS,
      MMAS4_INTERVAL_DAYS,
      (chk) => chk.get('reportedAt'),
      (chk) => chk.get('reportedAtTZ'),
      (chk) => 4 - mmas4Score(chk.get('mmas4'))
    );
    const baseline = this.getBaselineValue(
      checkins,
      (chk) => chk.get('reportedAt'),
      (chk) => chk.get('reportedAtTZ'),
      (chk) => 4 - mmas4Score(chk.get('mmas4'))
    );
    
    return (
      <div>
        <p className="section-description"
           style={fonts.description}>
          {strings.patient.mmas4Desc}
        </p>
        {this.renderBarGraph({
          values: vals,
          yMin: 0,
          yMax: 4,
          numYAxisUnits: 4,
          yAxisLabels: { min: '4', max: '0', main: 'Score' },
          showBaseline: baseline !== null && baseline !== undefined,
          baselineVal: baseline,
          xMin: 0,
          xMax: NUM_DAYS,
          xRel: this.day(),
          numXAxisUnits: NUM_DAYS/MMAS4_INTERVAL_DAYS,
          xAxisValues: xAxisValues(NUM_DAYS, MMAS4_ANCHOR_INTERVAL_DAYS),
          colorsForValue: () => {
            return { body: 'rgba(203, 255, 15, 0.2)', marker: '#A8E603' };
          }
        })}
      </div>
    );
  };

  renderSleep = () => {
    const { patient } = this.props;
    const emas = ( patient && patient.get('emaCheckins') ) || List();
    const vals = this.getTimeAdjustedValues(
      emas,
      SLEEP_INTERVAL_DAYS,
      SLEEP_INTERVAL_DAYS,
      (ema) => ema.get('reportedAt'),
      (ema) => ema.get('reportedAtTZ'),
      (ema) => {
        return { quality: ema.get('sleepQuality'), numHours: ema.get('sleepAmount') };
      }
    );
    return (
      <div>
        {this.renderKey([
          { name: 'Good', color: colorsForSleepQuality(SleepQualityAnswers.GOOD).marker },
          { name: 'Neutral', color: colorsForSleepQuality(SleepQualityAnswers.NEUTRAL).marker },
          { name: 'Poor', color: colorsForSleepQuality(SleepQualityAnswers.POOR).marker },
        ])}
        {this.renderBarGraph({
          values: vals,
          yMin: 0,
          yMax: 24,
          numYAxisUnits: 6,
          yAxisLabels: { min: '0', max: '24', main: 'Hours' },
          xMin: 0,
          xMax: NUM_DAYS,
          xRel: this.day(),
          numXAxisUnits: NUM_DAYS/SLEEP_INTERVAL_DAYS,
          xAxisValues: xAxisValues(NUM_DAYS, SLEEP_ANCHOR_INTERVAL_DAYS),
          yValueForValue: ((sleepObj) => sleepObj && sleepObj.numHours),
          colorsForValue: ((sleepObj) => sleepObj && colorsForSleepQuality(sleepObj.quality))
        })}
      </div>
    );
  };

  renderActivity = () => {
    const { patient } = this.props;
    const emas = ( patient && patient.get('emaCheckins') ) || List();
    const stepVals = this.getTimeAdjustedValues(
      emas,
      ACTIVITY_STEPS_INTERVAL_DAYS,
      ACTIVITY_STEPS_INTERVAL_DAYS,
      (ema) => ema.get('reportedAt'),
      (ema) => ema.get('reportedAtTZ'),
      (ema) => null // TODO: read data when its sent to us
    );
    const activityVals = this.getTimeAdjustedValues(
      emas,
      ACTIVITY_INTERVAL_DAYS,
      ACTIVITY_INTERVAL_DAYS,
      (ema) => ema.get('reportedAt'),
      (ema) => ema.get('reportedAtTZ'),
      (ema) => ema.get('physicalActiveness')
    );
    return (
      <div>
        {this.renderKey([
          { name: 'More than my "norm"', color: colorsForActivity(PhysicalActiveness.EXCEED_NORM).marker },
          { name: 'My normal amount', color: colorsForActivity(PhysicalActiveness.NORMAL).marker },
          { name: 'Less than my "norm"', color: colorsForActivity(PhysicalActiveness.LESS_THAN_NORM).marker },
          { name: 'None', color: colorsForActivity(PhysicalActiveness.NONE).marker }
        ])}
        {this.renderBarGraph({
          values: stepVals,
          yMin: 0,
          yMax: 10000,
          numYAxisUnits: 4,
          yAxisLabels: { min: '0', max: '10k', main: 'Steps' },
          numXAxisUnits: NUM_DAYS/ACTIVITY_STEPS_INTERVAL_DAYS,
          xMin: 0,
          xMax: NUM_DAYS,
          xRel: this.day(),
          xAxisValues: xAxisValues(NUM_DAYS, ACTIVITY_ANCHOR_INTERVAL_DAYS),
          showBaseline: false,
          baselineVal: null, // TODO: show if we have baseline
          colorsForValue: () => {
            return { body: 'rgba(203, 255, 15, 0.2)', marker: '#A8E603' };
          }
        })}
        {this.renderSingleValueChart({
          values: activityVals,
          colorForValue: (val) => val && colorsForActivity(val).marker,
          numXAxisUnits: NUM_DAYS/ACTIVITY_INTERVAL_DAYS,
          xMin: 0,
          xMax: NUM_DAYS,
          xRel: this.day()
        })}
      </div>
    );
  };

  renderAppetite = () => {
    const { patient } = this.props;
    const emas = ( patient && patient.get('emaCheckins') ) || List();
    const statusVals = this.getTimeAdjustedValues(
      emas,
      APPETITE_INTERVAL_DAYS,
      APPETITE_INTERVAL_DAYS,
      (ema) => ema.get('reportedAt'),
      (ema) => ema.get('reportedAtTZ'),
      (ema) => ema.get('appetiteStatus')
    );
    const amountVals = this.getTimeAdjustedValues(
      emas,
      APPETITE_INTERVAL_DAYS,
      APPETITE_INTERVAL_DAYS,
      (ema) => ema.get('reportedAt'),
      (ema) => ema.get('reportedAtTZ'),
      (ema) => ema.get('appetiteAmount')
    );

    const questionStyle = {
      fontSize: 14,
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      fontWeight: 'bold',
      color: '#666666',
      marginLeft: Y_AXIS_WIDTH,
      paddingRight: 5,
      display: 'flex',
      flex: 1,
      textAlign: 'left'
    };
    const renderQuestionLabel = (q) => ( <p style={questionStyle}>{q}</p> )

    return (
      <div>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          {renderQuestionLabel('How has your appetite been?')}
          {this.renderKey([
            { name: 'Increased', color: colorsForAppetiteStatus(AppetiteStatus.INCREASED).marker },
            { name: 'Normal', color: colorsForAppetiteStatus(AppetiteStatus.NORMAL).marker },
            { name: 'Decreased', color: colorsForAppetiteStatus(AppetiteStatus.DECREASED).marker }
          ])}
        </div>
        {this.renderSingleValueChart({
          values: statusVals,
          colorForValue: (val) => val && colorsForAppetiteStatus(val).marker,
          numXAxisUnits: NUM_DAYS/APPETITE_INTERVAL_DAYS,
          xMin: 0,
          xMax: NUM_DAYS,
          xRel: this.day()
        })}
        {this.renderXAxis(xAxisValues(NUM_DAYS, APPETITE_ANCHOR_INTERVAL_DAYS))}
        {this.renderSingleValueChart({
          values: amountVals,
          colorForValue: (val) => val && colorsForAppetiteAmount(val).marker,
          numXAxisUnits: NUM_DAYS/APPETITE_INTERVAL_DAYS,
          xMin: 0,
          xMax: NUM_DAYS,
          xRel: this.day()
        })}
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          {renderQuestionLabel('How much have you been eating?')}
          {this.renderKey([
            { name: 'More than normal', color: colorsForAppetiteAmount(AppetiteAmount.MORE_THAN_NORM).marker },
            { name: 'My normal amount', color: colorsForAppetiteAmount(AppetiteAmount.NORM).marker },
            { name: 'Less than normal', color: colorsForAppetiteAmount(AppetiteAmount.LESS_THAN_NORM).marker }
          ])}
        </div>
      </div>
    );
  };

  // helpers
  startDay = () => {
    if ( !this.props.patient ) return 0;
    return moment(this.props.patient.get('createdAt')).tz(this.props.patient.get('createdAtTZ')).startOf('day');
  };

  day = () => {
    const patient = this.props.patient;
    if ( !patient ) return 0;
    return moment().diff( moment(patient.get('createdAt')).tz(patient.get('createdAtTZ')).startOf('day'), 'days') + 1;
  };
  
  getTimeAdjustedValues = (objs, interval, lag, getObjTimestamp, getObjTZ, getValue ) => {
    const startDate = this.startDay();
    return Array(NUM_DAYS/interval).fill(null).map((_, i) => {
      const minDay = lag + interval*i;
      const maxDay = minDay + interval;
      const obj = objs.find((o) => {
        const calDay = moment(getObjTimestamp(o)).tz(getObjTZ(o)).startOf('day');
        const day = calDay.diff(startDate, 'days');
        return day >= minDay && day < maxDay;
      });
      return obj ? getValue(obj) : null;
    });
  };

  getBaselineValue = (objs, getObjTimestamp, getObjTZ, getValue ) => {
    const startDate = this.startDay();
    const obj = objs.find((o) => {
      const calDay = moment(getObjTimestamp(o)).tz(getObjTZ(o)).startOf('day');
      const day = calDay.diff(startDate, 'days');
      return day === 0;
    });
    return obj ? getValue(obj) : null;
  };

  renderControl = (title, key, onUpdateControl) => {
    const val = this.props.controls.get(key);
    return (
      <div className="control-container">
        <p className="secondary-header">{title.toUpperCase()}</p>
        <div className="control-btn-container">
          {ControlValues.map((_val, idx) => {
            const baseDivClassName = idx === 0 ? "control-btn" : "control-btn bordered-ctrl-btn";
            const divClassName = val === _val ? baseDivClassName + " ctrl-btn-sel" : baseDivClassName;
            const labelClassName = val === _val ? "ctrl-btn-label-sel" : "ctrl-btn-label-unsel";
            return (
              <div className={divClassName}
                   onClick={onUpdateControl.bind(null, this.props.controls.set(key, _val))}>
                <p className={labelClassName}>{_val.toUpperCase()}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  
  // renderers
  renderBarGraph(config) {
    const { xAxisValues, yAxisLabels, ...barConfig } = config;
    return (
      <div className="full-graph-container">
        <div className="graph-y-container">
          {renderYAxis(yAxisLabels)}
          {this.renderInnerBarGraph(barConfig)}
        </div>
        {this.renderXAxis(xAxisValues)}
      </div>
    );
  }
  
  renderInnerBarGraph(config) {
    const {
      values,
      yValueForValue,
      colorsForValue,
      
      yMin, yMax,
      numYAxisUnits,
      
      xMin, xMax, xRel,
      numXAxisUnits,

      showBaseline,
      baselineVal
    } = config;
    
    const renderYAxisDivs = () => (
      <div className="y-axis-container">
        {Array(numYAxisUnits).fill(null).map((_, i) => {
          return (
            <div className="y-axis-divider" key={i}/>
          );
        })}
      </div>
    );

    const xUnits = Array(numXAxisUnits).fill(null);
    const renderMarker = (color, key) => (
        <div className="bar-marker"
             style={{ backgroundColor: color, height: 2 }}
             key={key} />
    );
    const isMissing = (val) => val === null || val === undefined;
    
    const renderXAxisDivs = () => (
      <div className="x-axis-container">
        <div className="baseline-x-axis-divider"
             style={ !showBaseline ? { backgroundColor: 'rgba(247,247,247,0.5)' } : null }>
          { !isMissing(baselineVal) ? (
              <div className="bar"
                   style={{ height: baselineVal === 0 ? 2 : `${baselineVal/yMax * 100}%`, backgroundColor: colorsForValue(baselineVal).body }}>
                {isMissing(baselineVal) ? null : renderMarker(colorsForValue(baselineVal).marker, 'baseline')}
              </div>
          ) : null}
        </div>
        {xUnits.map((_, i) => {
          const _val = values[i];
          const val = yValueForValue ? yValueForValue(_val) : _val;
          const colors = colorsForValue(_val);
          return (
            <div className="x-axis-divider"
                 key={i}>
              <div className="bar"
                   style={val === yMin || isMissing(val) ? null : { height: `${val/yMax * 100}%`, backgroundColor: colors.body }}>
                {isMissing(val) ? null : renderMarker(colors.marker, i)}
              </div>
            </div>
          );
        })}
      </div>
    );

    return (
      <div className="graph-container">
        {this.renderRelativeIndicator(xRel, xMax)}
        {renderYAxisDivs()}
        {renderXAxisDivs()}
      </div>
    );
  }

  renderKey(items) {
    return (
      <div style={{display: 'flex', flex: 1, flexDirection: 'row'}}>
        <div style={styles.spacer}/>
          <div className="key-container" style={{display: 'flex', flexWrap: 'wrap'}}>
            {items.map((item, idx) => (
                <div className="key-item"
                     style={{ marginRight: items.length-1 === idx ? 0 : 30 }}
                     key={item.name}>
                  <div className="key-dot"
                       style={{ backgroundColor: item.color }} />
                  <p style={{ ...fonts.description, marginTop: 0, marginBottom: 0}}>
                    {item.name}
                  </p>
                </div>
            ))}
          </div>
        <div style={styles.spacer}/>
      </div>
    );
  }

  renderXAxis(labels){
    return (
      <div className="x-axis-label-container">
        <div style={{marginLeft: '11%', display: 'flex', justifyContent: 'flex-end'}}>
          <p style={fonts.xAxisSmall}>
            0wk
          </p>
        </div>
        {labels.map((label) => (
          <div className="x-axis-label"
               key={label}>
            <p style={fonts.xAxisSmall}>
              {label}
            </p>
          </div>
        ))}
      </div>
    );
  }

  renderSingleValueChart(config) {
    const { values, xRel, xMax, numXAxisUnits, colorForValue, showBaseline, baselineVal } = config;
    const xUnits = Array(numXAxisUnits).fill(null);
    const renderXAxisDivs = () => (
      <div className="x-axis-container">
        <div className="baseline-x-axis-divider"
             style={ !showBaseline ? { backgroundColor: 'rgba(247,247,247,0.5)' } : null }>
          { baselineVal ? (
              <div className="value-dot"
                   style={!baselineVal ? null : { backgroundColor: colorForValue(baselineVal) }} />
          ) : null}
        </div>
        {xUnits.map((_, i) => {
          const val = values[i];
          const color = colorForValue(val);
          return (
            <div className="x-axis-divider"
                 key={i}>
              <div className="value-dot"
                    style={!val ? null : { backgroundColor: color }} />
            </div>
          );
        })}
      </div>
    );

    return (
      <div className="full-graph-container"
           style={{ height: 25, marginLeft: Y_AXIS_WIDTH }}>
        <div className="graph-container">
          {this.renderRelativeIndicator(xRel, xMax)}
          {renderXAxisDivs()}
        </div>
      </div>
    );
  }

  renderHorizontalBarGraph(config) {
    const { values, numXAxisUnits, xMin, xMax, xRel, colorForValue, xValueForValue, showBaseline, baselineXValueForValue, yAxisLabels } = config;
    const xUnit = xMax/numXAxisUnits;

    const renderYAxisDivs = () => (
      <div className="y-axis-container">
        {Array(values.count).fill(null).map((_, i) => {
          return (
            <div className="y-axis-divider" key={i}/>
          );
        })}
      </div>
    );

    const renderYSection = (val, valGetter, i) => {
      const percentage = val ? valGetter(val) : 0;
      return (
        <div style={{ display: 'flex', flex: 1, width: '100%', alignItems: 'center' }}
             key={`${val}` + `${i}`}>
          {val ? (
              <div className="horiz-bar"
                   style={{ backgroundColor:colorForValue(val), width: `${percentage * 100}%` }} />
          ) : null}
        </div>
      );
    };
    const xUnits = Array(numXAxisUnits).fill(null);
    const isMissing = (val) => val === null || val === undefined;
    
    const renderXAxisDivs = () => (
      <div className="x-axis-container">
        <div className="baseline-x-axis-divider"
             style={ !showBaseline ? { backgroundColor: 'rgba(247,247,247,0.5)' } : { display: 'flex', flexDirection: 'column' }}>
          {showBaseline ? (
            values.map((val) => {
              return renderYSection(val, baselineXValueForValue);
            })) : null}
        </div>
        {xUnits.map((_, i) => {
          return (
            <div className="x-axis-divider"
                 style={{ display: 'flex', flexDirection: 'column' }}
                 key={i}>
              {values.map((val) => {
                return renderYSection(val, (v) => xValueForValue(v, i), i);
              })}
            </div>
          );
        })}
      </div>
    );
    
    return (
      <div className="full-graph-container"
           style={{ height: 63 }}>
        <div className="graph-y-container">
          {renderYAxis(yAxisLabels)}
        <div className="graph-container"
                   style={{ height: 63 }}>
            {this.renderRelativeIndicator(xRel, xMax)}
            {renderXAxisDivs()}
            {renderYAxisDivs()}
          </div>
        </div>
      </div>
    );
  }

  renderRelativeIndicator(_xVal, maxX) {
    const xVal = _xVal || 0;
    return (
      <div className="relative-indicator-container">
        <div className="relative-indicator"
             style={{ width: `${(1 - (xVal/maxX)) * 100}%`  }} />
      </div>
    );
  }
}

function renderYAxis(yAxisLabels) {
  return (
      <div className="y-axis-label-container"
           style={{ width: 10 }}>
        <div className="y-axis-top-label-container">
          <p style={{ ...fonts.yAxis, marginTop: -6 }}>
            {yAxisLabels.max}
          </p>
        </div>
        <div className="y-axis-main-label-container">
          <p className="y-axis-main-label"
             style={fonts.yAxisMain}>
             {yAxisLabels.main.toUpperCase()}
          </p>
        </div>
        <div className="y-axis-bottom-label-container">
          <p style={{ ...fonts.yAxis, marginBottom: -5 }}>
            {yAxisLabels.min}
          </p>
        </div>
      </div>
  );
};

function xAxisValues(maxDays, intervalDays) {
  const max = maxDays / 7;
  const interval = intervalDays / 7;
  return Array(max/interval).fill(null).map((_,i) => `${interval*i+interval}wk`);
}



// WEBPACK FOOTER //
// ./src/components/patient.js